export const normalizeRoutes = (data) => {
  if (Array.isArray(data)) return data;
  return data?.data || data?.routes || data?.items || [];
};

export const normalizeRoute = (data) => {
  if (!data) return null;
  if (Array.isArray(data)) return data[0] || null;
  if (data._id || data.id || data.title || data.name) return data;
  return data.data || data.route || null;
};

export const emptyPoint = () => ({
  title: "",
  lat: "",
  lng: "",
});

export const emptyStage = () => ({
  _id: "",
  stage_number: "",
  stage_name: "",
  startingPoint: emptyPoint(),
  endingPoint: emptyPoint(),
  stage_description: "",
});

export const initialRouteForm = {
  title: "",
  total_distance_km: "",
  estimated_time: "",
  description: "",
  status: "active",
  track_map_image: "",
  trackMapFile: null,
  stages: [],
};

const parsePoint = (point) => {
  if (!point || typeof point !== "object") return emptyPoint();
  return {
    title: point.title || "",
    lat:
      point.lat === 0 || point.lat
        ? String(point.lat)
        : "",
    lng:
      point.lng === 0 || point.lng
        ? String(point.lng)
        : "",
  };
};

const getStagePoint = (stage, camelKey, snakeKey) => {
  return parsePoint(stage?.[camelKey] || stage?.[snakeKey]);
};

export const mapStageToForm = (stage) => ({
  _id: stage?._id || stage?.id || "",
  stage_number:
    stage?.stage_number === 0 || stage?.stage_number
      ? String(stage.stage_number)
      : "",
  stage_name: stage?.stage_name || "",
  startingPoint: getStagePoint(stage, "startingPoint", "starting_point"),
  endingPoint: getStagePoint(stage, "endingPoint", "ending_point"),
  stage_description: stage?.stage_description || "",
});

export const mapRouteToForm = (route) => ({
  title: route?.title || route?.name || "",
  total_distance_km:
    route?.total_distance_km === 0 || route?.total_distance_km
      ? String(route.total_distance_km)
      : "",
  estimated_time: route?.estimated_time || "",
  description: route?.description || "",
  status: route?.status === "inactive" ? "inactive" : "active",
  track_map_image: route?.track_map_image || route?.trackMapImage || "",
  trackMapFile: null,
  stages: Array.isArray(route?.stages)
    ? route.stages.map(mapStageToForm)
    : [],
});

const isBlankPoint = (point) => {
  if (!point) return true;
  return !(
    String(point.title || "").trim() ||
    String(point.lat ?? "").trim() ||
    String(point.lng ?? "").trim()
  );
};

const isBlankStage = (stage) => {
  if (!stage) return true;
  return !(
    String(stage.stage_number || "").trim() ||
    String(stage.stage_name || "").trim() ||
    String(stage.stage_description || "").trim() ||
    !isBlankPoint(stage.startingPoint) ||
    !isBlankPoint(stage.endingPoint)
  );
};

export const getRouteApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  const message = data?.message || error?.message || fallback;

  if (Array.isArray(data?.errors) && data.errors.length) {
    return `${message}: ${data.errors.join(", ")}`;
  }

  if (typeof data?.error === "string" && data.error) {
    return data.error;
  }

  return message;
};

const validatePoint = (point, label) => {
  if (isBlankPoint(point)) return null;

  const errors = {};
  if (!String(point.title || "").trim()) {
    errors.title = `${label} title is required.`;
  }

  const lat = Number(point.lat);
  if (
    point.lat === "" ||
    Number.isNaN(lat) ||
    !Number.isFinite(lat) ||
    lat < -90 ||
    lat > 90
  ) {
    errors.lat = "Enter a valid latitude (-90 to 90).";
  }

  const lng = Number(point.lng);
  if (
    point.lng === "" ||
    Number.isNaN(lng) ||
    !Number.isFinite(lng) ||
    lng < -180 ||
    lng > 180
  ) {
    errors.lng = "Enter a valid longitude (-180 to 180).";
  }

  return Object.keys(errors).length ? errors : null;
};

export const validateRouteForm = (form) => {
  const errors = {};

  if (!form.title?.trim()) {
    errors.title = "Title is required.";
  }

  const distance = Number(form.total_distance_km);
  if (
    form.total_distance_km === "" ||
    Number.isNaN(distance) ||
    !Number.isFinite(distance) ||
    distance < 0
  ) {
    errors.total_distance_km = "Enter a valid distance in km.";
  }

  if (form.status && form.status !== "active" && form.status !== "inactive") {
    errors.status = "Status must be active or inactive.";
  }

  const stageErrors = {};
  const seenNumbers = new Map();
  (form.stages || []).forEach((stage, index) => {
    if (isBlankStage(stage)) return;

    const rawNumber = String(stage.stage_number ?? "").trim();
    if (!rawNumber) {
      stageErrors[index] = {
        ...(stageErrors[index] || {}),
        stage_number: "Stage number is required.",
      };
    } else {
      const stageNumber = Number(rawNumber);
      if (!Number.isInteger(stageNumber) || stageNumber < 0) {
        stageErrors[index] = {
          ...(stageErrors[index] || {}),
          stage_number: "Enter a valid stage number.",
        };
      } else if (seenNumbers.has(stageNumber)) {
        const message = "Stage numbers must be unique.";
        stageErrors[index] = {
          ...(stageErrors[index] || {}),
          stage_number: message,
        };
        const firstIndex = seenNumbers.get(stageNumber);
        stageErrors[firstIndex] = {
          ...(stageErrors[firstIndex] || {}),
          stage_number: message,
        };
      } else {
        seenNumbers.set(stageNumber, index);
      }
    }

    const startingPointErrors = validatePoint(
      stage.startingPoint,
      "Starting point"
    );
    if (startingPointErrors) {
      stageErrors[index] = {
        ...(stageErrors[index] || {}),
        startingPoint: startingPointErrors,
      };
    }

    const endingPointErrors = validatePoint(stage.endingPoint, "Ending point");
    if (endingPointErrors) {
      stageErrors[index] = {
        ...(stageErrors[index] || {}),
        endingPoint: endingPointErrors,
      };
    }
  });

  if (Object.keys(stageErrors).length) {
    errors.stages = stageErrors;
  }

  return errors;
};

const buildPointPayload = (point) => {
  if (isBlankPoint(point)) return null;
  return {
    title: String(point.title || "").trim(),
    lat: Number(point.lat),
    lng: Number(point.lng),
  };
};

export const buildStagesPayload = (stages = [], { includeIds = false } = {}) => {
  return stages
    .filter((stage) => !isBlankStage(stage))
    .map((stage) => {
      const item = {
        stage_number: Number(stage.stage_number),
      };

      if (includeIds && stage._id) {
        item._id = stage._id;
      }

      if (String(stage.stage_name || "").trim()) {
        item.stage_name = stage.stage_name.trim();
      }

      if (String(stage.stage_description || "").trim()) {
        item.stage_description = stage.stage_description.trim();
      }

      const startingPoint = buildPointPayload(stage.startingPoint);
      if (startingPoint) {
        item.startingPoint = startingPoint;
      }

      const endingPoint = buildPointPayload(stage.endingPoint);
      if (endingPoint) {
        item.endingPoint = endingPoint;
      }

      return item;
    });
};

export const buildRouteJsonPayload = (form, { isEdit = false } = {}) => {
  const distance = Number(form.total_distance_km);
  const status = form.status === "inactive" ? "inactive" : "active";

  const payload = {
    title: form.title.trim(),
    total_distance_km: distance,
    status,
  };

  if (form.estimated_time?.trim()) {
    payload.estimated_time = form.estimated_time.trim();
  } else if (isEdit) {
    payload.estimated_time = "";
  }

  if (form.description?.trim()) {
    payload.description = form.description.trim();
  } else if (isEdit) {
    payload.description = "";
  }

  const stages = buildStagesPayload(form.stages, { includeIds: isEdit });
  if (stages.length) {
    payload.stages = stages;
  }

  return payload;
};

export const buildRouteCreatePayload = (form) => {
  const trackMapFile =
    form.trackMapFile instanceof File ? form.trackMapFile : null;
  const jsonPayload = buildRouteJsonPayload(form, { isEdit: false });

  if (!trackMapFile) {
    return jsonPayload;
  }

  const formData = new FormData();
  formData.append("title", jsonPayload.title);
  formData.append("total_distance_km", String(jsonPayload.total_distance_km));
  formData.append("status", jsonPayload.status);

  if (jsonPayload.estimated_time) {
    formData.append("estimated_time", jsonPayload.estimated_time);
  }
  if (jsonPayload.description) {
    formData.append("description", jsonPayload.description);
  }
  if (jsonPayload.stages?.length) {
    formData.append("stages", JSON.stringify(jsonPayload.stages));
  }
  formData.append("track_map_image", trackMapFile);

  return formData;
};

export const buildTrackMapFormData = (file) => {
  const formData = new FormData();
  formData.append("image", file);
  return formData;
};

export const filterRoutes = (routes, searchTerm) => {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return routes;

  return routes.filter((route) => {
    const title = route?.title?.toLowerCase() || "";
    const description = route?.description?.toLowerCase() || "";
    const estimated = route?.estimated_time?.toLowerCase() || "";
    return (
      title.includes(term) ||
      description.includes(term) ||
      estimated.includes(term)
    );
  });
};
